import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../library/components/d-popup.js';
import '../../library/elements/d-section.js';
import '../../library/fields/d-view-info.js';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import { t } from 'src/i18n';
import type { RecurrenceOptionsInput } from './recurrence-options-input';
import type { RecurrenceOptionsResult } from './recurrence-options-result';
import type { DialogActionInput } from '../../library/components/DialogActionInput';

/**
 *
 * USAGE:
 *    base-event-editor
 *
 */
@customElement('recurrence-options-dialog')
export class RecurrenceOptionsDialog extends BaseDialog<RecurrenceOptionsInput, RecurrenceOptionsResult> {
  @property({ type: Boolean })
  deleting = false;
  @property({ type: Boolean })
  isMeeting = false;
  width = 400;

  protected get calculatedTitle(): string {
    if (this.deleting) {
      return this.calculatedDeletingTitle();
    } else {
      return this.calculatedEditingTitle();
    }
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('events.recurrenceOptionsCancel'), action: 'cancel' },
      {
        name: this.isMeeting ? t('events.recurrenceOptionsOnlyThisMeeting') : t('events.recurrenceOptionsOnlyThisTask'),
        action: 'single',
      },
      { name: t('events.recurrenceOptionsAll'), action: 'following' },
    ];
  }

  private get text() {
    if (this.deleting) {
      if (this.isMeeting) {
        return t('events.recurrenceOptionsDeleteMeetingText');
      } else {
        return t('events.recurrenceOptionsDeleteTaskText');
      }
    } else if (this.isMeeting) {
      return t('events.recurrenceOptionsEditMeetingText');
    } else {
      return t('events.recurrenceOptionsEditTaskText');
    }
  }

  renderBody() {
    return html`
      <d-section>
        <d-view-info .content=${this.text}></d-view-info>
      </d-section>
    `;
  }

  protected fetchResult(detail: string | undefined): RecurrenceOptionsResult {
    switch (detail) {
      case 'following':
        return { action: 'following' };
      case 'single':
        return { action: 'single' };
      case 'cancel':
        return { action: 'cancel' };
    }
    if (detail === undefined) {
      return { action: 'cancel' };
    }
    throw new Error('Method not implemented.');
  }

  protected initializeDialog(input: RecurrenceOptionsInput) {
    this.deleting = input.deleting;
    this.isMeeting = input.isMeeting;
  }

  private calculatedEditingTitle() {
    if (this.isMeeting) {
      return t('events.recurrenceOptionsEditMeetingTitle');
    } else {
      return t('events.recurrenceOptionsEditTaskTitle');
    }
  }

  private calculatedDeletingTitle() {
    if (this.isMeeting) {
      return t('events.recurrenceOptionsDeleteMeetingTitle');
    } else {
      return t('events.recurrenceOptionsDeleteTaskTitle');
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'recurrence-options-dialog': RecurrenceOptionsDialog;
  }
}
